import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Markdown } from './Markdown';
import {
  DEFAULT_DOC_SLUG,
  getDocBySlug,
  getDocContent,
  getDocsForProduct,
  getProductForSlug,
  type DocItem,
} from './manifest';
import { useDocsScroll } from './DocsScrollContext';

const APPEND_ROOT_MARGIN = '800px 0px';
const ACTIVE_ROOT_MARGIN = '-35% 0px -55% 0px';

export function DocsPage() {
  const { slug } = useParams<{ slug: string }>();
  const effectiveSlug = slug ?? DEFAULT_DOC_SLUG;
  const startDoc = getDocBySlug(effectiveSlug);

  if (!startDoc) {
    return <Navigate to={`/docs/${DEFAULT_DOC_SLUG}`} replace />;
  }

  return <DocsScrollStream key={startDoc.slug} startSlug={startDoc.slug} />;
}

function DocsScrollStream({ startSlug }: { startSlug: string }) {
  const productDocs = useMemo(() => {
    const product = getProductForSlug(startSlug);
    return product ? getDocsForProduct(product.id) : [];
  }, [startSlug]);
  const startIndex = useMemo(
    () => productDocs.findIndex(d => d.slug === startSlug),
    [productDocs, startSlug]
  );

  const [visibleCount, setVisibleCount] = useState(1);
  const visibleDocs = useMemo(
    () => productDocs.slice(startIndex, startIndex + visibleCount),
    [productDocs, startIndex, visibleCount]
  );
  const hasMore = startIndex + visibleCount < productDocs.length;

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const { setActiveSlug, scrollRequestRef } = useDocsScroll();

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisibleCount(c => Math.min(c + 1, productDocs.length - startIndex));
          }
        }
      },
      { rootMargin: APPEND_ROOT_MARGIN }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, productDocs.length, startIndex, visibleCount]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const intersecting = entries.filter(e => e.isIntersecting);
        if (intersecting.length === 0) return;
        const top = intersecting.reduce((best, e) => {
          const t = e.boundingClientRect.top;
          return !best || t < best.boundingClientRect.top ? e : best;
        }, intersecting[0]);
        const slug = (top.target as HTMLElement).dataset.slug;
        if (slug) setActiveSlug(slug);
      },
      { rootMargin: ACTIVE_ROOT_MARGIN, threshold: 0 }
    );

    sectionRefs.current.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [visibleCount, setActiveSlug]);

  const scrollToLoadedSlug = useCallback((slug: string): boolean => {
    const el = sectionRefs.current.get(slug);
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }, []);

  useEffect(() => {
    scrollRequestRef.current = scrollToLoadedSlug;
    return () => {
      scrollRequestRef.current = () => false;
    };
  }, [scrollRequestRef, scrollToLoadedSlug]);

  useEffect(() => {
    setActiveSlug(startSlug);
  }, [startSlug, setActiveSlug]);

  const registerSection = useCallback((slug: string, el: HTMLElement | null) => {
    if (el) sectionRefs.current.set(slug, el);
    else sectionRefs.current.delete(slug);
  }, []);

  return (
    <div className="docs__stream">
      {visibleDocs.map(doc => (
        <DocsSection key={doc.slug} doc={doc} registerSection={registerSection} />
      ))}
      {hasMore && <div ref={sentinelRef} className="docs__sentinel" aria-hidden="true" />}
    </div>
  );
}

interface DocsSectionProps {
  doc: DocItem;
  registerSection: (slug: string, el: HTMLElement | null) => void;
}

function DocsSection({ doc, registerSection }: DocsSectionProps) {
  const content = getDocContent(doc.file);
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    registerSection(doc.slug, ref.current);
    return () => registerSection(doc.slug, null);
  }, [doc.slug, registerSection]);

  return (
    <article
      ref={ref}
      id={doc.slug}
      data-slug={doc.slug}
      className="docs__article docs__article--streamed"
    >
      {content ? (
        <Markdown>{content}</Markdown>
      ) : (
        <div className="docs__missing">
          <h1>{doc.title}</h1>
          <p>This page has no content yet.</p>
        </div>
      )}
    </article>
  );
}
